import { askEndpoints, isMainlandRegion } from './askSite.js';
import { reactive } from 'vue';

// Keep the request attached through catalog selection, composition, and the
// illustration. Provider retries and a structural repair share this deadline.
const VIEW_TIMEOUT_MS = Number(process.env.VUE_APP_VIEW_TIMEOUT_MS || 180000);
const VIEW_JOB_KEY = 'henry.viewJob.v1';
const VIEW_JOB_POLL_MS = 1800;

// Site-wide compose state: only one View may be composing at a time, and every
// surface (homepage Ask offer, search palette, the View composer on /views)
// shares the same lock so a second composition cannot start mid-flight.
const activeViewComposes = new Set();

// Reactive snapshot of the site-wide compose so every surface can mirror the
// running composition (same animation, same started-at time) and lock its own
// input until it finishes.
export const viewComposeState = reactive({
  active: false,
  surface: '',
  startedAt: 0,
  label: '',
  progress: null,
});

export function beginViewCompose(surface, startedAt = Date.now(), label = '') {
  const key = String(surface || 'unknown');
  activeViewComposes.add(key);
  viewComposeState.active = true;
  viewComposeState.surface = key;
  viewComposeState.startedAt = Number(startedAt) || Date.now();
  viewComposeState.label = String(label || '');
  viewComposeState.progress = null;
}

export function endViewCompose(surface) {
  activeViewComposes.delete(String(surface || 'unknown'));
  if (activeViewComposes.size === 0) {
    viewComposeState.active = false;
    viewComposeState.surface = '';
    viewComposeState.startedAt = 0;
    viewComposeState.label = '';
    viewComposeState.progress = null;
  }
}

export function isViewComposing() {
  return viewComposeState.active;
}

export function isDevelopMode(text) {
  return /<develop>/i.test(String(text || ''));
}

export const DEVELOP_VIEW_PROMPT = 'How do Search, Ask, and View work together across Henry’s site?';
export const DEVELOP_VIEW_LABEL = 'Search, Ask, and View';
export const DEVELOP_VIEW_ROUTE = '/view/mWJh9RnvLDsTCRfp';
export const DEVELOP_VIEW_DELAY_MS = 6000;

export function developViewDelay(signal) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, DEVELOP_VIEW_DELAY_MS);
    if (signal) {
      signal.addEventListener('abort', () => {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    }
  });
}

// <develop> is an author-only simulated composition trigger. It never reaches
// retrieval, a model request, or a saved View snapshot.
export function cleanViewIntent(text) {
  const raw = String(text || '');
  const intent = raw.replace(/<develop>/gi, '').trim();
  return intent || (isDevelopMode(raw) ? DEVELOP_VIEW_PROMPT : '');
}

function announceViewJob(detail) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('site-view-job', { detail }));
}

function storeViewJob(task) {
  if (typeof window === 'undefined') return task;
  try { window.localStorage.setItem(VIEW_JOB_KEY, JSON.stringify(task)); } catch { /* Storage is best effort. */ }
  return task;
}

export function loadViewJob() {
  if (typeof window === 'undefined') return null;
  try {
    const task = JSON.parse(window.localStorage.getItem(VIEW_JOB_KEY) || 'null');
    return task?.version === 1 && task.jobId ? task : null;
  } catch { return null; }
}

export function clearViewJob(jobId = '') {
  const task = loadViewJob();
  if (task && jobId && task.jobId !== jobId) return;
  try { window.localStorage.removeItem(VIEW_JOB_KEY); } catch { /* Storage is best effort. */ }
}

function timeoutSignal(ms, externalSignal) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  const abortFromExternal = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', abortFromExternal, { once: true });
  }
  return {
    signal: controller.signal,
    cancel: () => {
      clearTimeout(timer);
      if (externalSignal) externalSignal.removeEventListener('abort', abortFromExternal);
    },
  };
}

function viewEndpoint() {
  if (isMainlandRegion()) return '';
  return askEndpoints()[0] || '';
}

async function postView(url, body, externalSignal) {
  // Large view-build payloads can hit a flaky transport (a dropped HTTP/3
  // upload) that fails before any HTTP response. Retry once so the request
  // reaches the worker; HTTP-level errors carry our own message and surface
  // as-is without a retry.
  let lastError = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const timeout = timeoutSignal(VIEW_TIMEOUT_MS, externalSignal);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: timeout.signal,
      });
      if (!res.ok) {
        let message = `View request failed at ${url}: ${res.status}`;
        let errorCode = '';
        try {
          const data = await res.json();
          errorCode = String(data?.error || '');
          if (data && data.message) message = data.message;
          else if (data && data.error) message = data.error;
          if (data && data.validation_error) {
            console.warn('AI View validation failed:', data.validation_error);
          }
        } catch {
          // Keep the HTTP fallback message.
        }
        if (attempt === 0 && [502, 503, 504].includes(res.status)) continue;
        const error = new Error(message);
        error.httpStatus = res.status;
        error.code = errorCode;
        throw error;
      }
      return await res.json();
    } catch (err) {
      if (err && err.name === 'AbortError') throw err;
      if (externalSignal && externalSignal.aborted) throw err;
      if (err?.httpStatus) throw err;
      const message = String((err && err.message) || '');
      const isHttpLevel = /^(View request failed|Could not compose a valid view)|temporarily unavailable in your region/.test(message);
      if (isHttpLevel) throw err;
      lastError = err;
      if (attempt === 0) continue;
      throw err;
    } finally {
      timeout.cancel();
    }
  }
  throw lastError || new Error('View request failed');
}

async function viewJobStatus(endpoint, jobId, externalSignal) {
  const response = await fetch(`${endpoint}/view-jobs/${encodeURIComponent(jobId)}`, { signal: externalSignal });
  if (!response.ok) {
    const error = new Error('Your saved View could not be found. Please compose it again.');
    error.code = response.status === 404 ? 'view_job_not_found' : 'view_job_unavailable';
    throw error;
  }
  return response.json();
}

async function waitForViewJob(endpoint, task, externalSignal) {
  for (;;) {
    if (externalSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const state = await viewJobStatus(endpoint, task.jobId, externalSignal);
    if (state.progress) viewComposeState.progress = { ...state.progress, receivedAt: Date.now() };
    if (state.status === 'complete') {
      const result = state.result || {};
      const payload = result.payload || {};
      if (Number(result.http_status) >= 400 || payload.error) {
        const error = new Error(payload.message || 'Your View could not be completed. Please try again.');
        error.code = payload.error || 'view_job_failed';
        error.httpStatus = Number(result.http_status) || 500;
        throw error;
      }
      const completed = storeViewJob({ ...task, status: 'complete', completedAt: Date.now(), payload });
      announceViewJob(completed);
      return payload;
    }
    if (state.status === 'failed') {
      const error = new Error(state.message || 'Your View could not be completed. Please try again.');
      error.code = 'view_job_failed';
      throw error;
    }
    if (!['composing', 'detached'].includes(task.status)) {
      task = storeViewJob({ ...task, status: 'composing' });
      announceViewJob(task);
    }
    await new Promise((resolve) => window.setTimeout(resolve, VIEW_JOB_POLL_MS));
  }
}

export async function resumeViewJob(task = loadViewJob(), signal) {
  const endpoint = viewEndpoint();
  if (!task || !endpoint) return null;
  return waitForViewJob(endpoint, task, signal);
}

export function cleanError(err) {
  if (err && err.name === 'AbortError') return 'Composition timed out. Please try again.';
  if (err?.code === 'view_selection_unavailable') return 'Your View could not be completed. Please try again.';
  if (err?.code === 'invalid_catalog') return 'Please refresh the site and try composing your View again.';
  if (err?.httpStatus === 429) return 'Please wait a moment before composing another View.';
  if (err && /currently unavailable in your region/.test(err.message)) return 'View is currently unavailable in your region.';
  if (err && /Could not compose a valid view/.test(err.message)) {
    return 'View composition is temporarily unavailable. Please try again.';
  }
  if (err && err.message && err.message.startsWith('View request failed')) {
    return 'View composition is temporarily unavailable. Please try again.';
  }
  return 'View composition is temporarily unavailable. Please try again.';
}

export async function requestView({ message, conversation, sources, meta, signal, returnContext }) {
  const endpoint = viewEndpoint();
  if (!endpoint) throw new Error('View composition is temporarily unavailable.');

  const payload = {
    task: 'build',
    message: String(message || '').slice(0, 800),
    conversation: Array.isArray(conversation) ? conversation.slice(-6) : [],
    sources: Array.isArray(sources) ? sources : [],
    meta: meta || {},
  };
  let submittedJobId = '';
  try {
    const started = await postView(`${endpoint}/view-jobs`, payload, signal);
    if (!started?.job_id) throw new Error('View composition could not be started.');
    submittedJobId = started.job_id;
    const task = storeViewJob({
      version: 1,
      jobId: started.job_id,
      status: 'composing',
      message: payload.message,
      surface: payload.meta.surface || 'view',
      originRoute: payload.meta.origin_route || (typeof window !== 'undefined' ? `${window.location.hash.slice(1) || '/home'}` : '/home'),
      returnContext: returnContext || null,
      startedAt: Date.now(),
    });
    announceViewJob(task);
    return await waitForViewJob(endpoint, task, signal);
  } catch (err) {
    if (err?.name === 'AbortError') {
      const task = loadViewJob();
      if (task?.status === 'composing') announceViewJob({ ...task, status: 'detached' });
      throw err;
    }
    if (signal?.aborted) throw err;
    if (submittedJobId) {
      clearViewJob(submittedJobId);
      announceViewJob({ jobId: submittedJobId, status: 'failed' });
    }
    const friendly = new Error(cleanError(err));
    friendly.code = err?.code || '';
    friendly.httpStatus = err?.httpStatus || 0;
    throw friendly;
  }
}
